// 完了トリガー方式のタスク生成ロジック

import { RepeatPattern, TaskInstance } from '../types';
import { parseApiDate } from './date';
import { WORKSPACE_DEFAULT_TIMEZONE } from './constants';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function normalizeLocalDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

// 月末日を取得
function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// 次回期限日を計算（完了日から次の該当日）
export function calculateNextDueDate(
  completionDate: Date,
  repeatPattern: RepeatPattern
): Date | null {
  // 繰り返しなしの場合はnullを返す
  if (repeatPattern.type === 'none') {
    return null;
  }

  // 初回締切日が設定されていない場合は計算不可
  if (!repeatPattern.initialDueDate) {
    console.error('initialDueDate is required for repeat pattern');
    return null;
  }

  const completion = normalizeLocalDate(completionDate);
  const initialDate = normalizeLocalDate(parseApiDate(repeatPattern.initialDueDate));
  
  let nextDate: Date;

  switch (repeatPattern.type) {
    case 'daily':
      // 完了日の翌日を次回期限日とする
      nextDate = new Date(completion);
      nextDate.setDate(nextDate.getDate() + 1);
      break;

    case 'weekly':
      // 初回締切日と同じ曜日で、完了日より後の最初の日付を見つける
      nextDate = new Date(initialDate);
      // 完了日より後になるまで1週間ずつ加算
      while (nextDate <= completion) {
        nextDate.setDate(nextDate.getDate() + 7);
      }
      break;

    case 'monthly':
      if (repeatPattern.useMonthEnd) {
        // 月末オプション: 完了日の翌月の最終日
        nextDate = new Date(completion);
        nextDate.setMonth(nextDate.getMonth() + 1);
        const lastDay = getLastDayOfMonth(nextDate.getFullYear(), nextDate.getMonth());
        nextDate.setDate(lastDay);
      } else {
        // 通常の月次: 初回締切日と同じ日にちで、完了日より後の最初の月
        const targetDay = initialDate.getDate();
        nextDate = new Date(completion);
        nextDate.setDate(1); // 一旦1日に設定
        nextDate.setMonth(nextDate.getMonth() + 1);
        
        // 目標日が存在するかチェック（例: 2月31日は存在しない）
        const lastDay = getLastDayOfMonth(nextDate.getFullYear(), nextDate.getMonth());
        nextDate.setDate(Math.min(targetDay, lastDay));
        
        // 完了日より後になるまで繰り返す
        while (nextDate <= completion) {
          nextDate.setMonth(nextDate.getMonth() + 1);
          const lastDayNext = getLastDayOfMonth(nextDate.getFullYear(), nextDate.getMonth());
          nextDate.setDate(Math.min(targetDay, lastDayNext));
        }
      }
      break;

    case 'quarterly':
      // 初回締切日から四半期（3ヶ月）単位で加算
      nextDate = new Date(initialDate);
      while (nextDate <= completion) {
        nextDate.setMonth(nextDate.getMonth() + 3);
      }
      break;

    case 'semi-annually':
      // 初回締切日から半年（6ヶ月）単位で加算
      nextDate = new Date(initialDate);
      while (nextDate <= completion) {
        nextDate.setMonth(nextDate.getMonth() + 6);
      }
      break;

    case 'yearly':
      // 初回締切日から年単位で加算
      nextDate = new Date(initialDate);
      while (nextDate <= completion) {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
      break;

    default:
      console.error('Unknown repeat type:', repeatPattern.type);
      return null;
  }

  return normalizeLocalDate(nextDate);
}

// 初回タスクの期限日を計算（初回締切日を返す）
export function calculateInitialDueDate(
  creationDate: Date,
  repeatPattern: RepeatPattern
): Date {
  if (!repeatPattern.initialDueDate) {
    // フォールバック: 翌日を返す
    const tomorrow = new Date(creationDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return normalizeLocalDate(tomorrow);
  }
  
  // 月末オプションの場合
  if (repeatPattern.useMonthEnd) {
    const now = new Date(creationDate);
    const lastDay = getLastDayOfMonth(now.getFullYear(), now.getMonth());
    const monthEndDate = new Date(now.getFullYear(), now.getMonth(), lastDay);
    return normalizeLocalDate(monthEndDate);
  }
  
  // 初回締切日を返す
  return normalizeLocalDate(parseApiDate(repeatPattern.initialDueDate));
}

function getDatePartsInTimeZone(date: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const getPartValue = (type: string): number => {
    const part = parts.find((item) => item.type === type);
    return part ? Number(part.value) : NaN;
  };

  const year = getPartValue('year') || date.getUTCFullYear();
  const month = getPartValue('month') || date.getUTCMonth() + 1;
  const day = getPartValue('day') || date.getUTCDate();

  return { year, month, day };
}

function normalizeDateInTimeZone(date: Date, timeZone?: string): Date {
  const tz = timeZone || WORKSPACE_DEFAULT_TIMEZONE;
  const { year, month, day } = getDatePartsInTimeZone(date, tz);
  return new Date(Date.UTC(year, month - 1, day));
}

export function getDateDiffInDays(targetDate: Date, referenceDate: Date, timeZone?: string): number {
  const normalizedTarget = normalizeDateInTimeZone(targetDate, timeZone);
  const normalizedReference = normalizeDateInTimeZone(referenceDate, timeZone);
  return Math.floor((normalizedTarget.getTime() - normalizedReference.getTime()) / MS_PER_DAY);
}

function getDateFromValue(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : parseApiDate(value);
}

// Task-like type for overdue checks (supports both Date and string dueDate)
interface TaskWithDueDate {
  status: string;
  dueDate?: Date | string | null;
}

// タスクが期限切れかどうか
export function isOverdue(task: TaskWithDueDate, timeZone?: string): boolean {
  if (task.status === 'completed') return false;
  const dueDateValue = getDateFromValue(task.dueDate);
  if (!dueDateValue) return false;

  const today = normalizeDateInTimeZone(new Date(), timeZone);
  const dueDate = normalizeDateInTimeZone(dueDateValue, timeZone);
  return today.getTime() > dueDate.getTime();
}

// 期限超過日数を計算
export function getOverdueDays(task: TaskWithDueDate, timeZone?: string): number {
  if (!task.dueDate) return 0;
  if (!isOverdue(task, timeZone)) return 0;

  const dueDateValue = getDateFromValue(task.dueDate);
  if (!dueDateValue) return 0;

  const today = normalizeDateInTimeZone(new Date(), timeZone);
  const dueDate = normalizeDateInTimeZone(dueDateValue, timeZone);
  const diff = today.getTime() - dueDate.getTime();
  return Math.floor(diff / MS_PER_DAY);
}

// 期限の説明テキストを生成
export function getRepeatPatternDescription(pattern: RepeatPattern): string {
  if (!pattern.initialDueDate) {
    return '繰り返しなし';
  }
  
  const initialDate = parseApiDate(pattern.initialDueDate);
  const dateStr = `${initialDate.getMonth() + 1}/${initialDate.getDate()}`;
  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
  const dayOfWeek = dayNames[initialDate.getDay()];
  
  switch (pattern.type) {
    case 'none':
      return '繰り返しなし';

    case 'daily':
      return `毎日（初回: ${dateStr} ${dayOfWeek}曜）`;
    
    case 'weekly':
      return `毎週${dayOfWeek}曜日（初回: ${dateStr}）`;
    
    case 'monthly':
      if (pattern.useMonthEnd) {
        return `毎月末日`;
      }
      return `毎月${initialDate.getDate()}日（初回: ${dateStr}）`;
    
    case 'quarterly':
      return `四半期ごと（初回: ${dateStr}）`;
    
    case 'semi-annually':
      return `半年ごと（初回: ${dateStr}）`;
    
    case 'yearly':
      return `毎年${initialDate.getMonth() + 1}月${initialDate.getDate()}日（初回: ${dateStr}）`;
    
    default:
      return '';
  }
}